import type { MapRuntimePort } from './map-runtime';

export type WeatherGrid = {
  width: number;
  height: number;
  values: Float32Array;
  unit: 'K' | 'raw';
  lo1: number;
  la1: number;
  dx: number;
  dy: number;
};

export type TemperatureLegend = { min: number; max: number } | null;
export type RadarCoverage = { percent: number; tiles: number; maxDbz?: number } | null;
export type RadarSamples = Record<number, Record<number, number>>;
export type WeatherBounds = { getWest(): number; getNorth(): number; getEast(): number; getSouth(): number };
export type WeatherFeatureCollection = { type: 'FeatureCollection'; features: any[] };
export type RadarRoute = { route_num: number; coordinates: Array<[number, number, number?]> };

type WeatherLayerRuntime = MapRuntimePort;

type RegisterLayerGroup = (id: string, apply: () => void | Promise<void>) => () => void;

export interface TemperatureLayerState {
  active: boolean;
  dataUrl: string | null;
  isolines: WeatherFeatureCollection | null;
  textFont: string[];
  grid: WeatherGrid | null;
}

export interface PressureLayerState { active: boolean; dataUrl: string | null }
export interface RadarLayerState { active: boolean; tileUrl: string | null; frameKey: string | number | null }
export interface WindLayerState { active: boolean; level: string; dataUrl: string }

export interface WindLayerResource {
  id: string;
  setData?(url: string): void | Promise<void>;
  setParticles?(count: number): void;
  setOpacity?(opacity: number): void;
  reseed?(): void;
  dispose?(): void;
}

export interface WindCanvasResource {
  setData?(url: string): void | Promise<void>;
  setParticles?(count: number): void;
  start?(): void;
  remove?(): void;
}

export interface WindPluginFactories {
  createLayer(options: Record<string, unknown>): WindLayerResource;
  createCanvas(runtime: WeatherLayerRuntime, options: Record<string, unknown>): WindCanvasResource;
}

export interface WeatherLayerDependencies {
  prepareWind?(): Promise<WindPluginFactories>;
  measureRadarCoverage?(tileUrl: string, bounds: WeatherBounds): Promise<RadarCoverage>;
  sampleRadarRoutes?(tileUrl: string, routes: RadarRoute[]): Promise<RadarSamples>;
}

export interface WeatherLayerEvents {
  onTemperatureLegend?(legend: TemperatureLegend): void;
  onRadarCoverage?(coverage: RadarCoverage): void;
  onUnavailable?(kind: 'wind' | 'radar' | 'temperature' | 'pressure', error?: unknown): void;
}

export interface WeatherLayerController {
  setEvents(events: WeatherLayerEvents): void;
  setTemperature(state: TemperatureLayerState): void;
  setPressure(state: PressureLayerState): void;
  setRadar(state: RadarLayerState): void;
  setWind(state: WindLayerState): Promise<void>;
  refreshViewport(): Promise<void>;
  sampleRoutes(frame: { tileUrl: string }, routes: RadarRoute[]): Promise<RadarSamples>;
  dispose(): void;
}

function sampleWeatherGrid(grid: WeatherGrid, lon: number, lat: number) {
  let gx = ((((lon - grid.lo1) % 360) + 360) % 360) / (grid.dx || 1);
  let gy = (grid.la1 - lat) / (grid.dy || 1);
  gy = Math.max(0, Math.min(grid.height - 1, gy));
  gx = Math.max(0, Math.min(grid.width - 1, gx));
  const x0 = Math.floor(gx);
  const x1 = (x0 + 1) % grid.width;
  const y0 = Math.floor(gy);
  const y1 = Math.min(y0 + 1, grid.height - 1);
  const fx = gx - x0;
  const fy = gy - y0;
  const values = grid.values;
  const v00 = values[y0 * grid.width + x0];
  const v10 = values[y0 * grid.width + x1];
  const v01 = values[y1 * grid.width + x0];
  const v11 = values[y1 * grid.width + x1];
  const value = v00 * (1 - fx) * (1 - fy) + v10 * fx * (1 - fy) + v01 * (1 - fx) * fy + v11 * fx * fy;
  return grid.unit === 'K' ? value - 273.15 : value;
}

export function viewportTemperatureRange(grid: WeatherGrid, bounds: WeatherBounds): TemperatureLegend {
  const values: number[] = [];
  for (let y = 0; y <= 5; y += 1) {
    const lat = bounds.getSouth() + ((bounds.getNorth() - bounds.getSouth()) * y) / 5;
    for (let x = 0; x <= 8; x += 1) {
      const lng = bounds.getWest() + ((bounds.getEast() - bounds.getWest()) * x) / 8;
      const value = sampleWeatherGrid(grid, lng, lat);
      if (Number.isFinite(value)) values.push(value);
    }
  }
  if (!values.length) return null;
  const min = Math.floor(Math.min(...values));
  const max = Math.ceil(Math.max(...values));
  return { min, max: Math.max(max, min + 1) };
}

function firstSymbolLayerId(runtime: WeatherLayerRuntime) {
  return runtime.getStyle()?.layers?.find((layer) => layer.type === 'symbol')?.id;
}

function removeLayer(runtime: WeatherLayerRuntime, id: string) {
  if (runtime.getLayer(id)) runtime.removeLayer(id);
}

function removeSource(runtime: WeatherLayerRuntime, id: string) {
  if (runtime.getSource(id)) runtime.removeSource(id);
}

function orderWeatherLayers(runtime: WeatherLayerRuntime) {
  const symbols = firstSymbolLayerId(runtime);
  if (runtime.getLayer('rainviewer-radar')) runtime.moveLayer('rainviewer-radar', symbols);
  const belowRain = runtime.getLayer('rainviewer-radar') ? 'rainviewer-radar' : symbols;
  if (runtime.getLayer('weather-pressure-raster')) runtime.moveLayer('weather-pressure-raster', belowRain);
  const belowPressure = runtime.getLayer('weather-pressure-raster') ? 'weather-pressure-raster' : belowRain;
  if (runtime.getLayer('weather-temp-raster')) runtime.moveLayer('weather-temp-raster', belowPressure);
  ['weather-temp-isotherm-shadow', 'weather-temp-isotherm-line', 'weather-temp-isotherm-label'].forEach((id) => {
    if (runtime.getLayer(id)) runtime.moveLayer(id, belowPressure);
  });
}

function windParticleCountForZoom(zoom: number) {
  return Math.round(Math.max(80000, Math.min(360000, 80000 + Math.max(0, zoom - 3) * 28000)));
}

function windOpacityForZoom(zoom: number) {
  return Math.max(0.55, Math.min(0.88, 0.55 + Math.max(0, zoom - 8) * 0.055));
}

function windCanvasParticleCountForZoom(zoom: number) {
  return Math.round(Math.max(2600, Math.min(7800, 2600 + Math.max(0, zoom - 10) * 1050)));
}

export function createWeatherLayerController(
  runtime: WeatherLayerRuntime,
  registerLayerGroup: RegisterLayerGroup,
  dependencies: WeatherLayerDependencies = {},
  initialEvents: WeatherLayerEvents = {}
): WeatherLayerController {
  let events = initialEvents;
  let temperature: TemperatureLayerState | null = null;
  let pressure: PressureLayerState | null = null;
  let radar: RadarLayerState | null = null;
  let wind: WindLayerState | null = null;
  let radarFrameKey: RadarLayerState['frameKey'] = null;
  let windFactories: WindPluginFactories | null = null;
  let windLayer: WindLayerResource | null = null;
  let windCanvas: WindCanvasResource | null = null;
  let disposed = false;
  let windApplication = 0;
  const rasterUrls = new Map<string, string>();

  const upsertRaster = (id: 'weather-temp' | 'weather-pressure', dataUrl: string | null, active: boolean, opacity: number) => {
    const sourceId = `${id}-src`;
    const layerId = `${id}-raster`;
    if (!active || !dataUrl) {
      removeLayer(runtime, layerId);
      removeSource(runtime, sourceId);
      return;
    }
    if (runtime.getSource(sourceId) && rasterUrls.get(sourceId) !== dataUrl) {
      removeLayer(runtime, layerId);
      removeSource(runtime, sourceId);
    }
    if (!runtime.getSource(sourceId)) {
      runtime.addSource(sourceId, { type: 'raster', tiles: [dataUrl], tileSize: 512, minzoom: 0, maxzoom: 0 });
      rasterUrls.set(sourceId, dataUrl);
    }
    if (!runtime.getLayer(layerId)) runtime.addLayer({
      id: layerId, type: 'raster', source: sourceId,
      paint: { 'raster-opacity': opacity, 'raster-fade-duration': 0 }
    }, firstSymbolLayerId(runtime));
  };

  const applyIsolines = () => {
    const state = temperature;
    const sourceId = 'weather-temp-isotherm-src';
    const layerIds = ['weather-temp-isotherm-shadow', 'weather-temp-isotherm-line', 'weather-temp-isotherm-label'];
    if (!state?.active || !state.isolines) {
      [...layerIds].reverse().forEach((id) => removeLayer(runtime, id));
      removeSource(runtime, sourceId);
      return;
    }
    const source = runtime.getSource(sourceId);
    if (source) source.setData(state.isolines);
    else runtime.addSource(sourceId, { type: 'geojson', data: state.isolines });
    if (!runtime.getLayer(layerIds[0])) runtime.addLayer({ id: layerIds[0], type: 'line', source: sourceId, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': 'rgba(255,255,255,0.9)', 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 2.4, 9, 3.4], 'line-blur': 1.2, 'line-opacity': 0.78 } }, firstSymbolLayerId(runtime));
    if (!runtime.getLayer(layerIds[1])) runtime.addLayer({ id: layerIds[1], type: 'line', source: sourceId, layout: { 'line-cap': 'round', 'line-join': 'round' }, paint: { 'line-color': 'rgba(15,23,42,0.72)', 'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.9, 9, 1.35], 'line-blur': 0.35, 'line-opacity': 0.86 } }, firstSymbolLayerId(runtime));
    if (!runtime.getLayer(layerIds[2])) runtime.addLayer({ id: layerIds[2], type: 'symbol', source: sourceId, minzoom: 4, layout: { 'symbol-placement': 'line', 'text-field': ['get', 'label'], 'text-size': ['interpolate', ['linear'], ['zoom'], 4, 10, 9, 12], 'text-font': state.textFont, 'text-allow-overlap': false, 'text-ignore-placement': false, 'text-padding': 4, 'text-rotation-alignment': 'map' }, paint: { 'text-color': '#0f172a', 'text-halo-color': 'rgba(255,255,255,0.92)', 'text-halo-width': 1.6, 'text-opacity': 0.88 } }, firstSymbolLayerId(runtime));
  };

  const applyTemperature = () => {
    if (disposed || !runtime.isStyleLoaded()) return;
    upsertRaster('weather-temp', temperature?.dataUrl ?? null, Boolean(temperature?.active), 0.76);
    applyIsolines();
    orderWeatherLayers(runtime);
  };

  const applyPressure = () => {
    if (disposed || !runtime.isStyleLoaded()) return;
    upsertRaster('weather-pressure', pressure?.dataUrl ?? null, Boolean(pressure?.active), 0.72);
    orderWeatherLayers(runtime);
  };

  const applyRadar = () => {
    if (disposed || !runtime.isStyleLoaded()) return;
    removeLayer(runtime, 'weather-precip-raster');
    removeSource(runtime, 'weather-precip-src');
    const state = radar;
    if (!state?.tileUrl) {
      if (runtime.getLayer('rainviewer-radar')) runtime.setPaintProperty('rainviewer-radar', 'raster-opacity', 0);
      return;
    }
    if (runtime.getSource('rainviewer-radar-source') && radarFrameKey !== state.frameKey) {
      removeLayer(runtime, 'rainviewer-radar');
      removeSource(runtime, 'rainviewer-radar-source');
    }
    radarFrameKey = state.frameKey;
    if (!runtime.getSource('rainviewer-radar-source')) runtime.addSource('rainviewer-radar-source', {
      type: 'raster', tiles: [state.tileUrl], tileSize: 512, minzoom: 0, maxzoom: 7,
      attribution: '<a href="https://www.rainviewer.com/">RainViewer</a>'
    });
    if (!runtime.getLayer('rainviewer-radar')) runtime.addLayer({
      id: 'rainviewer-radar', type: 'raster', source: 'rainviewer-radar-source',
      paint: { 'raster-opacity': state.active ? 0.72 : 0, 'raster-contrast': 0.35, 'raster-saturation': 0.25, 'raster-fade-duration': 0 }
    }, firstSymbolLayerId(runtime));
    else runtime.setPaintProperty('rainviewer-radar', 'raster-opacity', state.active ? 0.72 : 0);
    orderWeatherLayers(runtime);
  };

  const removeWindCanvas = () => {
    windCanvas?.remove?.();
    windCanvas = null;
  };

  const removeWindLayer = (destroy = false) => {
    if (windLayer && runtime.getLayer(windLayer.id)) runtime.removeLayer(windLayer.id);
    if (destroy) windLayer?.dispose?.();
    if (destroy) windLayer = null;
  };

  const applyWind = async () => {
    const application = ++windApplication;
    const desired = wind;
    const isCurrent = () => !disposed && desired?.active === true && wind === desired && application === windApplication;
    if (disposed) return;
    if (!desired?.active) {
      removeWindLayer();
      removeWindCanvas();
      return;
    }
    try {
      windFactories ||= await dependencies.prepareWind?.() ?? null;
      if (!isCurrent()) return;
      if (!windFactories) throw new Error('Wind plugin unavailable');
      const zoom = runtime.getZoom();
      if (zoom >= 10) {
        removeWindLayer();
        if (!isCurrent()) return;
        windCanvas ||= windFactories.createCanvas(runtime, { particles: windCanvasParticleCountForZoom(zoom), speed: 0.72, maxAge: 24, fade: 0.9, width: 0.85, color: 'rgba(37,99,235,0.42)' });
        windCanvas.setParticles?.(windCanvasParticleCountForZoom(zoom));
        await windCanvas.setData?.(desired.dataUrl);
        if (!isCurrent()) return;
        windCanvas.start?.();
        return;
      }
      removeWindCanvas();
      if (!isCurrent()) return;
      windLayer ||= windFactories.createLayer({ id: 'wind-particles', particles: windParticleCountForZoom(zoom), speed: 1.13, maxAge: 15, pointSize: 1.5, opacity: windOpacityForZoom(zoom) });
      windLayer.setParticles?.(windParticleCountForZoom(zoom));
      windLayer.setOpacity?.(windOpacityForZoom(zoom));
      await windLayer.setData?.(desired.dataUrl);
      if (!isCurrent()) return;
      if (!runtime.getLayer(windLayer.id)) runtime.addLayer(windLayer);
      windLayer.reseed?.();
    } catch (error) {
      if (isCurrent()) events.onUnavailable?.('wind', error);
    }
  };

  const replay = async () => {
    applyTemperature();
    applyPressure();
    applyRadar();
    await applyWind();
  };
  const unregister = registerLayerGroup('weather', replay);

  return {
    setEvents(next) { events = next; },
    setTemperature(state) {
      temperature = state;
      applyTemperature();
      events.onTemperatureLegend?.(state.grid ? viewportTemperatureRange(state.grid, runtime.getBounds()) : null);
    },
    setPressure(state) { pressure = state; applyPressure(); },
    setRadar(state) { radar = state; applyRadar(); },
    async setWind(state) { wind = state; await applyWind(); },
    async refreshViewport() {
      if (disposed) return;
      events.onTemperatureLegend?.(temperature?.grid ? viewportTemperatureRange(temperature.grid, runtime.getBounds()) : null);
      if (radar?.active && radar.tileUrl && dependencies.measureRadarCoverage) {
        try { events.onRadarCoverage?.(await dependencies.measureRadarCoverage(radar.tileUrl, runtime.getBounds())); }
        catch (error) { events.onRadarCoverage?.(null); events.onUnavailable?.('radar', error); }
      } else events.onRadarCoverage?.(null);
      await applyWind();
    },
    async sampleRoutes(frame, routes) {
      if (!dependencies.sampleRadarRoutes) return {};
      return dependencies.sampleRadarRoutes(frame.tileUrl, routes);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      windApplication += 1;
      unregister();
      removeWindCanvas();
      removeWindLayer(true);
    }
  };
}
